import React, { useState, useEffect } from 'react';
import { Container, Header, Segment, Divider, Button, Modal, Icon, Step, Card, Progress } from 'semantic-ui-react';
import '@styles/BlogBody.css';
import '@styles/GeneralTheme.css';
import WebToLead from '@components/WebToLead';

const HowWeHelp = () => {
  const [modalOpen, setModalOpen] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);

  // the required distance between touchStart and touchEnd to be detected as a swipe
  const minSwipeDistance = 50;

  const onTouchStart = (e) => {
    setTouchEnd(null); // otherwise the swipe is fired even with usual touch events
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e) => setTouchEnd(e.targetTouches[0].clientX);

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;
    if (isLeftSwipe && currentStep < steps.length) {
      nextStep();
    }
    if (isRightSwipe && currentStep > 0) {
      prevStep();
    }
  };

  // Add Open Graph meta tags for how we help page
  useEffect(() => {
    const updateMetaTag = (property, content, isProperty = true) => {
      const attribute = isProperty ? 'property' : 'name';
      let metaTag = document.querySelector(`meta[${attribute}="${property}"]`);
      
      if (!metaTag) {
        metaTag = document.createElement('meta');
        metaTag.setAttribute(attribute, property);
        document.head.appendChild(metaTag);
      }
      metaTag.setAttribute('content', content);
    };

    document.title = 'How We Help - Cloud CZR | Salesforce Development & Technology Consulting';
    updateMetaTag('og:title', 'How We Help - Cloud CZR | Salesforce Development & Technology Consulting');
    updateMetaTag('og:description', 'Learn how Cloud CZR helps clients with Salesforce solutions, technology consulting, and strategic guidance for business success.');
    updateMetaTag('og:url', window.location.href);
    updateMetaTag('og:type', 'website');
    updateMetaTag('og:image', `${window.location.origin}/assets/CloudChiRho.png`);
    updateMetaTag('og:image:alt', 'Cloud CZR, Cloudczr, cloudczr.com');
    
    updateMetaTag('description', 'Learn how Cloud CZR helps clients with Salesforce solutions, technology consulting, and strategic guidance for business success.', false);
    updateMetaTag('twitter:title', 'How We Help - Cloud CZR | Salesforce Development & Technology Consulting', false);
    updateMetaTag('twitter:description', 'Learn how Cloud CZR helps clients with Salesforce solutions, technology consulting, and strategic guidance for business success.', false);
  }, []);

  const handleOpenModal = () => {
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
  };

  const steps = [
    {
      title: 'Attracting Qualified Leads',
      pain: 'Few qualified leads coming in',
      solution: 'We attract the right people using targeted online ads, social media, and influencer outreach — then capture their info through easy website forms or landing pages.'
    },
    {
      title: 'Organizing Lead Flow',
      pain: 'Leads get lost or forgotten',
      solution: 'Everything flows into one organized system that tracks every contact automatically — no more dropping balls.'
    },
    {
      title: 'Integrating Marketing & Sales',
      pain: 'Marketing and sales tools don\'t work together',
      solution: 'We connect your website, ads, email, texts, and phone systems so data moves smoothly and your team sees the full picture.'
    },
    {
      title: 'Converting Prospects',
      pain: 'Customers drop off before buying',
      solution: 'We send timely, personalized follow-ups via email, text, or calls to build trust and guide them to purchase — increasing conversions.'
    },
    {
      title: 'Automating Operations',
      pain: 'Too much manual work slows your team',
      solution: 'We automate repetitive tasks (like data entry, alerts, and updates) so your people focus on selling, not admin.'
    },
    {
      title: 'Ensuring Security & Compliance',
      pain: 'Worried about data security or compliance',
      solution: 'We add strong protection, encryption, and safe practices to keep information secure and meet requirements.'
    },
    {
      title: 'Measuring Success',
      pain: 'Hard to measure what\'s working',
      solution: 'We provide clear tracking and insights so you see exactly which efforts bring results — and can improve fast.'
    }
  ];

  const nextStep = () => {
    if (currentStep < steps.length) {
      setCurrentStep(currentStep + 1);
    }
  };

  const prevStep = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const goToStep = (stepIndex) => {
    setCurrentStep(stepIndex);
  };

  return (
    <Container fluid style={{ marginTop: '2em', padding: '0 2em', maxWidth: 'none !important', width: '100% !important' }}>
      <Header as='h1' textAlign='center' style={{ color: '#a9885c', marginBottom: '1em' }}>
        How We Help
      </Header>
      <p style={{ textAlign: 'center', color: '#702963', fontSize: '1.3em', fontWeight: 'bold', marginBottom: '2em' }}>
        Your Common Pain Points & How We Fix Them
      </p>

      <Segment style={{ backgroundColor: 'white', padding: '2em', borderRadius: '8px', marginBottom: '2em' }}>
        <Header as='h2' textAlign='center' style={{ color: '#702963', marginBottom: '1em' }}>
          <Icon name='road' />
          Navigate Your Business Growth Funnel
        </Header>
        <p style={{ textAlign: 'center', color: '#a9885c', fontSize: '1.1em', marginBottom: '2em' }}>
          These fixes come from years of hands-on experience building and optimizing these exact systems for businesses in banking, debt resolution, private equity, and more. Results: faster leads, smoother operations, higher sales.
        </p>

        {/* Progress Indicator */}
        <div style={{ marginBottom: '2em' }}>
          <Progress 
            value={currentStep + 1} 
            total={steps.length + 1} 
            size='small' 
            color='purple'
            style={{ marginBottom: '1em' }}
          />
          <div style={{ textAlign: 'center', color: '#702963', fontSize: '0.9em' }}>
            Step {currentStep + 1} of {steps.length + 1}
          </div>
        </div>

        {/* Step Navigation */}
        <div style={{ 
          display: 'flex', 
          flexDirection: 'column',
          alignItems: 'stretch',
          gap: '0.5em',
          marginBottom: '2em'
        }}>
          {steps.map((step, index) => (
            <div 
              key={index} 
              onClick={() => goToStep(index)}
              style={{ 
                padding: '0.5em',
                borderRadius: '4px',
                backgroundColor: currentStep === index ? '#702963' : currentStep > index ? '#a9885c' : '#f9f9fa',
                color: currentStep === index || currentStep > index ? '#ffffff' : '#702963',
                textAlign: 'left',
                cursor: 'pointer',
                border: '1px solid #e9ecef',
                fontSize: '0.9em',
                fontWeight: 'bold',
                minHeight: '50px',
                display: 'flex',
                alignItems: 'center',
                paddingLeft: '1em'
              }}
            >
              {index + 1}. {step.title}
            </div>
          ))}
          <div 
            style={{ 
              padding: '0.5em',
              borderRadius: '4px',
              backgroundColor: currentStep === steps.length ? '#a9885c' : '#f9f9fa',
              color: currentStep === steps.length ? '#702963' : '#702963',
              textAlign: 'left',
              border: '1px solid #e9ecef',
              fontSize: '0.9em',
              fontWeight: 'bold',
              minHeight: '50px',
              display: 'flex',
              alignItems: 'center',
              paddingLeft: '1em'
            }}
          >
            8. Complete
          </div>
        </div>

        {/* Step Content */}
        {currentStep < steps.length ? (
          <Card 
            fluid 
            style={{ 
              backgroundColor: '#f9f9fa', 
              border: '2px solid #e9ecef',
              padding: window.innerWidth < 768 ? '0.5em' : '1em'
            }}
            onTouchStart={onTouchStart}
            onTouchMove={onTouchMove}
            onTouchEnd={onTouchEnd}
          >
            <Card.Content
              onTouchStart={onTouchStart}
              onTouchMove={onTouchMove}
              onTouchEnd={onTouchEnd}
            >
              <Card.Header style={{ color: '#702963', fontSize: '1.5em', marginBottom: '1em' }}>
                <Icon name='exclamation triangle' />
                {steps[currentStep].title}
              </Card.Header>
              
              <div style={{ display: 'flex', flexDirection: window.innerWidth < 768 ? 'column' : 'row', gap: window.innerWidth < 768 ? '0.5em' : '1em', alignItems: 'flex-start' }}>
                <div style={{ backgroundColor: '#ffebee', flex: 1, padding: '1em', borderRadius: '0.28571429rem' }}>
                  <Header as='h4' style={{ color: '#c62828' }}>
                    <Icon name='warning sign' />
                    The Pain Point
                  </Header>
                  <p style={{ color: '#c62828', fontSize: '1.1em' }}>
                    {steps[currentStep].pain}
                  </p>
                </div>

                <div style={{ backgroundColor: '#e8f5e8', flex: 1, padding: '1em', borderRadius: '0.28571429rem' }}>
                  <Header as='h4' style={{ color: '#2e7d32' }}>
                    <Icon name='check circle' />
                    Our Solution
                  </Header>
                  <p style={{ color: '#2e7d32', fontSize: '1.1em' }}>
                    {steps[currentStep].solution}
                  </p>
                </div>
              </div>
            </Card.Content>
          </Card>
        ) : (
          <Card fluid style={{ backgroundColor: '#f9f9fa', border: '2px solid #a9885c', padding: window.innerWidth < 768 ? '0.5em' : '1em' }}>
            <Card.Content textAlign='center'>
              <Icon name='trophy' size='huge' style={{ color: '#a9885c', marginBottom: '1em' }} />
              <Card.Header style={{ color: '#702963', fontSize: '2em', marginBottom: '1em' }}>
                All Pain Points Addressed
              </Card.Header>
              <Card.Description style={{ color: '#a9885c', fontSize: '1.2em', marginBottom: '2em' }}>
                From attracting qualified leads to measuring success, we've systematically addressed every common business challenge. 
                Ready to implement these proven solutions and transform your operations?
              </Card.Description>
              <Button 
                primary 
                size='huge' 
                onClick={handleOpenModal}
                style={{ backgroundColor: '#702963', borderColor: '#702963' }}
              >
                <Icon name='rocket' />
                Start Your Journey Today
              </Button>
            </Card.Content>
          </Card>
        )}

        {/* Navigation Buttons */}
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          marginTop: '2em',
          flexDirection: window.innerWidth < 768 ? 'column' : 'row',
          gap: window.innerWidth < 768 ? '1em' : '0'
        }}>
          <Button 
            secondary 
            onClick={prevStep} 
            disabled={currentStep === 0}
            style={{ 
              backgroundColor: currentStep === 0 ? '#ccc' : '#f9f9fa', 
              color: '#702963',
              width: window.innerWidth < 768 ? '100%' : 'auto'
            }}
          >
            <Icon name='arrow left' />
            Previous
          </Button>
          
          {currentStep < steps.length ? (
            <Button 
              primary 
              onClick={nextStep}
              style={{ 
                backgroundColor: '#702963', 
                borderColor: '#702963',
                width: window.innerWidth < 1024 ? '100%' : 'auto'
              }}
            >
              Next
              <Icon name='arrow right' />
            </Button>
          ) : (
            <Button 
              primary 
              onClick={() => setCurrentStep(0)}
              style={{ 
                backgroundColor: '#a9885c', 
                borderColor: '#a9885c', 
                color: '#702963',
                width: window.innerWidth < 768 ? '100%' : 'auto'
              }}
            >
              <Icon name='refresh' />
              Start Over
            </Button>
          )}
        </div>
      </Segment>

      {/* Contact Form Modal */}
      <Modal open={modalOpen} onClose={handleCloseModal} size='large' style={{ backgroundColor: 'white' }}>
        <Modal.Header style={{ backgroundColor: '#702963', color: '#a9885c', textAlign: 'center' }}>
          <Icon name='envelope' />
          Contact Us
        </Modal.Header>
        <Modal.Content style={{ backgroundColor: 'white' }}>
          <WebToLead />
        </Modal.Content>
        <Modal.Actions style={{ backgroundColor: '#f9f9f9', textAlign: 'center' }}>
          <Button 
            onClick={handleCloseModal}
            style={{ backgroundColor: '#702963', color: '#a9885c' }}
          >
            Close
          </Button>
        </Modal.Actions>
      </Modal>
    </Container>
  );
};

export default HowWeHelp;